import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { debounce, isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../core/modules/global';
import { UPDATE_BUSINESS_DIALOG } from '../../../../core/constants/dialog';
import Spinner from '../../Spinner';
import { useCompanyCore } from '../../../../core/modules/company';
import { useBusinessCore } from '../../../../core/modules/business';
import { DISTRICTS } from '../../../../core/constants/chileDistrict';
import Swal from 'sweetalert2';
import { useMeCore } from '../../../../core/modules/me';
import { transformRUT } from '../../../../core/utils/utils';
import { validateRUT } from '../../../../core/utils/utils';
import { BusinessArea } from '../../../../core/interfaces/business.interface';
import { calculateUsedCommission } from '../../../../core/modules/business/helpers/commission-calculator';
import { ERROR } from '../../../../core/constants/errorCode';

const schema = yup.object().shape({
  name: yup.string().required('Campo requerido'),
  rut: yup
    .string()
    .required('Campo requerido')
    .test('test-rut', 'Rut inválido. Ej: 11.111.111-K', function (value) {
      return validateRUT(value);
    })
    .transform((v) => transformRUT(v)),
  acronym: yup
    .string()
    .test('len', '3 carácteres requeridos', (val) => !val || val.length === 3)
    .optional(),
  business_name: yup.string().required('Campo requerido'),
  district: yup.string().required('Campo requerido'),
  street: yup.string().required('Campo requerido'),
  number: yup.string().required('Campo requerido'),
  phone: yup.string().required('Campo requerido'),
  contact_name: yup.string().required('Campo requerido'),
  contact_email: yup
    .string()
    .email('Formato incorrecto')
    .required('Campo requerido'),
  unit: yup.string().optional(),
  comments: yup.string().optional(),
});

const UpdateDialog = () => {
  const { dialogs, closeDialog } = useGlobalCore();
  const { user } = useMeCore();
  const {
    business,
    putBusiness,
    deleteBusiness,
    getBusinesses,
    setBusiness,
    putArea,
    errorCode,
    clearBusiness,
  } = useBusinessCore();
  
  // Guardar una referencia al business cuando se abre el diálogo
  const [savedBusiness, setSavedBusiness] = useState<typeof business>(null);
  const { companyId } = useCompanyCore();

  const [loading, setLoading] = useState(false);
  const [originalAcronym, setOriginalAcronym] = useState<string>('');

  const canWrite = useMemo(
    () => user && (user.privileges.superAdmin || user.privileges.writeBusiness),
    [user]
  );

  // Encontrar el área que tiene el mismo acrónimo que la empresa
  const findBusinessMainArea = useCallback(
    (businessData: any): BusinessArea | null => {
      return (
        businessData?.areas?.find(
          (area: BusinessArea) => area.acronym === businessData.acronym
        ) || null
      );
    },
    []
  );

  // Validar si se puede eliminar la empresa
  const canDeleteBusiness = useMemo(() => {
    if (!business) return false;
    const mainArea = findBusinessMainArea(business);
    // No se puede eliminar si tiene un área con el mismo acrónimo y 100% de comisión
    return !(mainArea && mainArea.commission === 100);
  }, [business, findBusinessMainArea]);

  const { control, formState, handleSubmit, resetField, setValue, reset, trigger } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const debouncedChangeHandler = useMemo(
    () =>
      debounce((value: string) => {
        if (validateRUT(value)) {
          setValue('rut', transformRUT(value));
        }
      }, 1000),
    [setValue]
  );

  const resetForm = useCallback(() => {
    resetField('acronym');
    resetField('business_name');
    resetField('comments');
    resetField('contact_email');
    resetField('contact_name');
    resetField('district');
    resetField('name');
    resetField('number');
    resetField('phone');
    resetField('rut');
    resetField('street');
    resetField('unit');
    setOriginalAcronym('');
    clearBusiness();
    setBusiness(null);
    closeDialog(UPDATE_BUSINESS_DIALOG);
  }, [closeDialog, resetField, setBusiness, clearBusiness]);

  // Limpiar error cuando se abre el diálogo (solo errorCode, no business)
  useEffect(() => {
    if (Array.isArray(dialogs) && dialogs.includes(UPDATE_BUSINESS_DIALOG) && business) {
      // Solo limpiar el errorCode, no el business
      // El errorCode se limpiará automáticamente cuando se llame a putBusiness
    }
  }, [dialogs, business]);

  useEffect(() => {
    if (business) {
      reset({
        name: business.name || '',
        rut: business.rut || '',
        acronym: business.acronym || undefined, // Usar undefined en lugar de string vacío
        business_name: business.business_name || '',
        district: business.district || '',
        street: business.street || '',
        number: business.number || '',
        phone: business.phone || '',
        contact_name: business.contact_name || '',
        contact_email: business.contact_email || '',
        unit: business.unit || '',
        comments: business.comments || '',
      }, { keepDefaultValues: false, keepErrors: false });

      // Guardar el acrónimo original para detectar cambios
      if (business.acronym) {
        setOriginalAcronym(business.acronym);
      } else {
        setOriginalAcronym('');
      }

      // Forzar validación después de reset
      setTimeout(() => {
        trigger();
      }, 100);
    }
  }, [business, reset, trigger]);

  const onSubmit = useCallback(
    (values: any) => {
      const { unit = '', comments = '', acronym, ...data } = values;
      // Usar savedBusiness si business es null
      const businessToUse = business || savedBusiness;
      if (companyId && businessToUse) {
        setLoading(true);

        // Calcular las áreas actualizadas para el payload
        const currentAreas = businessToUse.areas || [];
        let mainArea = findBusinessMainArea(businessToUse);

        // Calcular la comisión del área principal
        let mainAreaCommission = 100;
        if (mainArea) {
          // Si existe área principal, calcular comisión basada en otras áreas
          const otherAreas = currentAreas.filter(
            (area) => area.id !== mainArea!.id
          );
          const otherAreasCommission = calculateUsedCommission(otherAreas);
          mainAreaCommission =
            (businessToUse.total_commission || 100) - otherAreasCommission;
        }

        // Preparar el array de áreas para el payload
        const areasPayload = currentAreas.map((area) => {
          if (area.acronym === originalAcronym || area.id === mainArea?.id) {
            // Esta es el área principal, usar el acrónimo existente y comisión calculada
            return {
              acronym: acronym,
              commission: mainAreaCommission,
            };
          }
          // Para otras áreas, mantener sus valores actuales
          return {
            acronym: area.acronym,
            commission: area.commission,
          };
        });

        // Si no existe área principal, crear una nueva
        if (!mainArea) {
          areasPayload.push({
            acronym: acronym,
            commission: businessToUse.total_commission || 100,
          });
        }

        // Preparar el payload completo de la empresa
        const businessPayload = {
          ...data,
          acronym,
          unit,
          comments,
          is_provider: true,
          total_commission: businessToUse.total_commission || 100,
          areas: areasPayload,
        };

        // Actualizar la empresa con el payload completo incluyendo áreas
        putBusiness(
          companyId,
          businessToUse.id,
          businessPayload,
          () => {
            resetForm();
            getBusinesses(companyId);
            setLoading(false);
          },
          () => {
            setLoading(false);
          }
        );
      }
    },
    [
      companyId,
      business,
      savedBusiness,
      putBusiness,
      resetForm,
      getBusinesses,
      originalAcronym,
      findBusinessMainArea,
    ]
  );

  const onDelete = useCallback(() => {
    if (!business) return;

    // Validar si se puede eliminar
    if (!canDeleteBusiness) {
      Swal.fire({
        title: 'No se puede eliminar',
        text: `No se puede eliminar la empresa ${business.name} porque tiene un área con el mismo acrónimo (${business.acronym}) que tiene el 100% de la comisión. Debe editar o eliminar primero las áreas.`,
        icon: 'error',
        confirmButtonText: 'Entendido',
      });
      return;
    }

    Swal.fire({
      title: 'Confirmar acción',
      text: `Eliminar la empresa ${business.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar!',
      cancelButtonText: 'Cancelar',
    }).then((result) => {
      if (result.isConfirmed && companyId && business) {
        setLoading(true);

        deleteBusiness(
          companyId,
          business.id,
          () => {
            setLoading(false);
            getBusinesses(companyId);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [
    business,
    companyId,
    deleteBusiness,
    getBusinesses,
    resetForm,
    canDeleteBusiness,
  ]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px] md:min-w-[700px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(UPDATE_BUSINESS_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar Empresa
              </Typography>
            </div>

            {business && !canDeleteBusiness && (
              <div className="mb-16 p-16 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Typography variant="body2" className="text-yellow-800">
                  ⚠️ <strong>Nota:</strong> Esta empresa no se puede eliminar
                  porque tiene un área con el mismo acrónimo ({business.acronym}
                  ) que tiene el 100% de la comisión. Para eliminar la empresa,
                  primero debe editar o eliminar las áreas.
                </Typography>
              </div>
            )}

            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="flex flex-col md:flex-row gap-32 mb-24">
                    <Controller
                      name="rut"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="RUT"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.rut}
                          onChange={(e) => {
                            field.onChange(e);
                            debouncedChangeHandler(e.target.value);
                          }}
                          helperText={errors?.rut?.message}
                          required
                          fullWidth
                        />
                      )}
                    />

                    {business?.acronym && (
                      <Controller
                        name="acronym"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Sigla"
                            type="text"
                            variant="outlined"
                            disabled={loading}
                            error={!!errors.acronym}
                            helperText={errors?.acronym?.message}
                            fullWidth
                            inputProps={{
                              maxLength: 3,
                              style: { textTransform: 'uppercase' }
                            }}
                            onChange={(e) => {
                              const value = e.target.value.toUpperCase();
                              field.onChange(value || undefined);
                            }}
                          />
                        )}
                      />
                    )}
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="business_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Razón Social"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.business_name}
                          helperText={errors?.business_name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="street"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Calle/Avenida"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.street}
                          helperText={errors?.street?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="number"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Número"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.number}
                          helperText={errors?.number?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="unit"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Oficina/Casa/Otro"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.unit}
                          helperText={errors?.unit?.message}
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <FormControl className="flex" variant="outlined" fullWidth>
                      <InputLabel id="district-label">Comuna *</InputLabel>
                      <Controller
                        name="district"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="district-label"
                            disabled={loading}
                            label="Comuna *"
                            required
                            fullWidth
                          >
                            {DISTRICTS.sort().map((district: string) => (
                              <MenuItem key={district} value={district}>
                                {district}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="phone"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Teléfono"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.phone}
                          helperText={errors?.phone?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="contact_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre de Contacto"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.contact_name}
                          helperText={errors?.contact_name?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="contact_email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Email de Contacto"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.contact_email}
                          helperText={errors?.contact_email?.message}
                          required
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="comments"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Comentarios"
                          variant="outlined"
                          multiline
                          rows={4}
                          disabled={loading}
                          error={!!errors.comments}
                          helperText={errors?.comments?.message}
                          fullWidth
                        />
                      )}
                    />
                  </div>

                  {errorCode && (
                    <Typography color="error" className="text-md text-center mb-8">
                      {errorCode === ERROR.ERR_409 
                        ? 'Ya existe una empresa con esta sigla o RUT' 
                        : errorCode === ERROR.ERR_400
                        ? 'Datos inválidos. Verifique los campos ingresados'
                        : 'Error al actualizar la empresa. Intente nuevamente'
                      }
                    </Typography>
                  )}

                  {canWrite && (
                    <div className="flex justify-center gap-4">
                      <Button
                        variant="contained"
                        color="error"
                        sx={{ borderRadius: 50 }}
                        disabled={loading || !canDeleteBusiness}
                        type="button"
                        onClick={onDelete}
                        title={
                          !canDeleteBusiness
                            ? `No se puede eliminar: área ${business?.acronym} tiene 100% de comisión`
                            : 'Eliminar empresa'
                        }
                      >
                        <Spinner show={loading} />
                        Eliminar
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        sx={{ borderRadius: 50 }}
                        disabled={!isEmpty(errors) || !isValid || loading}
                        type="submit"
                        size="large"
                      >
                        <Spinner show={loading} />
                        Guardar
                      </Button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
